import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import TableBox from '../../../components/TableBox';
import StatusBadge from '../../../components/StatusBadge';
import RowActions from '../../../components/RowActions';
import Button from '../../../components/Button';
import Dropdown from '../../../components/Dropdown';
import Toast from '../../../components/Toast';
import useFetch from '../../../hooks/useFetch';
import usePagination from '../../../hooks/usePagination';
import PaymentService from '../../../services/payment.service';
import TenantService from '../../../services/tenant.service';
import useSorting from '../../../hooks/useSorting';
import { sortData } from '../../../utils/sortUtils';
import { HeadCellType, TableContextProvider } from '../../../interfaces/common';
import { SortOrder, Modules, AuthRoutes, RouteParamsTypes } from '../../../enums';


interface PaymentRow {
  id: number;
  receipt_no: string;
  tenant_name: string | null;
  subscription_no: string | null;
  amount: string;
  currency_code: string | null;
  currency_symbol: string | null;
  payment_date: string;
  payment_mode: string;
  receipt_url: string | null;
}

interface TenantOption {
  id: number;
  tenant_name: string;
}

const PaymentList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const subscriptionIdFilter = searchParams.get('subscriptionId');

  const { page, perPage, setPage, setPerPage } = usePagination({ defaultPage: 1, defaultPerPage: 10 });
  const { order, orderBy, setOrder, setOrderBy } = useSorting({ defaultOrder: SortOrder.desc, defaultOrderBy: 'payment_date' });
  const [items, setItems] = useState<PaymentRow[]>([]);
  const [total, setTotal] = useState(0);
  const [tenantFilter, setTenantFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [resendingId, setResendingId] = useState<number | null>(null);

  useEffect(() => {
    TenantService.list({ includeDeleted: false, limit: 100 }).then((response: any) => {
      setTenants(response?.data?.items ?? []);
    });
  }, []);

  const { loading } = useFetch({
    service: PaymentService,
    func: 'list',
    payload: {
      page,
      limit: perPage,
      tenantId: tenantFilter ? Number(tenantFilter) : undefined,
      subscriptionId: subscriptionIdFilter ? Number(subscriptionIdFilter) : undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    },
    dependency: [page, perPage, tenantFilter, dateFrom, dateTo, subscriptionIdFilter],
    onSuccess: (data: any) => {
      setItems(data?.items ?? []);
      setTotal(data?.pagination?.total ?? 0);
    },
    onError: () => {
      setItems([]);
      setTotal(0);
    },
  });

  const handleResend = useCallback(
    async (id: number) => {
      setResendingId(id);
      try {
        await PaymentService.resend(id);
        toast.success(<Toast message={t('payments.list.pageTexts.resendSuccess')} />, { icon: false });
      } finally {
        setResendingId(null);
      }
    },
    [t]
  );

  const headCells: HeadCellType[] = useMemo(
    () => [
      { id: 'receipt_no', sortEnabled: true, width: '13%', label: t('payments.list.pageTexts.table.columns.receiptNo') },
      { id: 'tenant_name', sortEnabled: true, width: '16%', label: t('payments.list.pageTexts.table.columns.tenant') },
      { id: 'subscription_no', sortEnabled: true, width: '13%', label: t('payments.list.pageTexts.table.columns.subscription') },
      { id: 'amount', sortEnabled: true, width: '11%', label: t('payments.list.pageTexts.table.columns.amount') },
      { id: 'payment_date', sortEnabled: true, width: '12%', label: t('payments.list.pageTexts.table.columns.paymentDate') },
      { id: 'payment_mode', sortEnabled: true, width: '12%', label: t('payments.list.pageTexts.table.columns.paymentMode') },
      { id: 'actions', sortEnabled: false, width: '23%', label: t('payments.list.pageTexts.table.columns.actions'), textAlign: 'right' },
    ],
    [t]
  );

  const sortedItems = useMemo(() => sortData(items, orderBy, order), [items, orderBy, order]);

  const Row = useCallback(
    ({ item, className }: { item: PaymentRow; className?: string }) => (
      <tr className={className}>
        <td>{item.receipt_no}</td>
        <td>{item.tenant_name ?? '—'}</td>
        <td>{item.subscription_no ?? '—'}</td>
        <td>
          {item.currency_symbol ?? ''}
          {item.amount}
        </td>
        <td>{String(item.payment_date).slice(0, 10)}</td>
        <td>{t(`payments.form.pageTexts.fields.paymentMode.options.${item.payment_mode}`)}</td>
        <td className="text-end">
          <RowActions
            actions={[
              Boolean(item.receipt_url) ? {
                id: 'download',
                label: t('common.download') || 'Download Receipt',
                icon: <i className="ri-download-2-line" />,
                onClick: () => window.open(`${process.env.REACT_APP_API_URL ?? ''}${item.receipt_url}`, '_blank'),
              } : null,
              {
                id: 'resend',
                label: t('payments.list.resendReceipt') || 'Resend Receipt',
                icon: <i className="ri-mail-send-line" />,
                onClick: () => handleResend(item.id),
              },
            ]}
          />
        </td>
      </tr>
    ),
    [handleResend, resendingId, t]
  );

  const values: TableContextProvider = useMemo(
    () => ({
      headCells,
      row: Row,
      order,
      orderBy,
      page,
      perPage,
      total,
      loading,
      data: sortedItems,
      pagination: true,
      noDataFoundMessage: t('payments.list.pageTexts.table.noDataFoundMessage'),
      setPage,
      setPerPage,
      setOrder,
      setOrderBy,
    }),
    [headCells, Row, order, orderBy, page, perPage, total, loading, sortedItems, setPage, setPerPage, setOrder, setOrderBy, t]
  );

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">{t('payments.list.pageTexts.title')}</h4>
        <Button
          className="btn btn-primary"
          text={t('payments.list.pageTexts.addNew')}
          onClick={() => navigate(`${Modules.home}${AuthRoutes.payments}${RouteParamsTypes.add}`)}
        />
      </div>

      <div className="row mb-4 g-2">
        <div className="col-md-4">
          <Dropdown
            name="tenantFilter"
            value={tenantFilter}
            placeholder={t('payments.list.pageTexts.filters.allTenants')}
            options={tenants.map((tItem) => ({ id: String(tItem.id), name: tItem.tenant_name }))}
            noSearch
            onChange={(e) => setTenantFilter(e.target.value)}
          />
        </div>
        <div className="col-md-4">
          <input
            type="date"
            className="form-control"
            aria-label={t('payments.list.pageTexts.filters.dateFrom')}
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>
        <div className="col-md-4">
          <input
            type="date"
            className="form-control"
            aria-label={t('payments.list.pageTexts.filters.dateTo')}
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>
      </div>

      <TableBox tableValues={values} />
    </>
  );
};

export default PaymentList;
